import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { getImageUrl, getPlaceholder } from '../api';
import { Loader, ServiceCard, Lightbox } from '../components/Shared';
import ConstructionCanvas from '../components/ConstructionCanvas';

const CRANE = '#d39816';

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lb, setLb] = useState({ open: false, index: 0 });

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${slug}`).then(r => setProject(r.data.data)).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ paddingTop: 120, background: 'var(--site-bg)', minHeight: '100vh' }}>
      <Loader />
    </div>
  );
  if (!project) return (
    <div style={{ background: 'var(--site-bg)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20 }}>
      <h3 style={{ color: 'var(--text)' }}>Project not found</h3>
      <Link to="/projects" className="btn-primary-pub">Back to Projects</Link>
    </div>
  );

  const allImages = [
    ...(project.thumbnail ? [getImageUrl('projects', project.thumbnail)] : []),
    ...(project.images || []).filter(img => img.filename !== project.thumbnail).map(img => getImageUrl('projects', img.filename)),
  ].filter(Boolean);

  const heroImg = allImages[0] || getPlaceholder(1200, 600, project.title);

  return (
    <>
      {/* Hero image */}
      <div className="project-detail-hero" style={{ height: '65vh', minHeight: 400, position: 'relative', overflow: 'hidden', background: 'var(--site-bg)' }}>
        <img src={heroImg} alt={project.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={e => e.target.src = getPlaceholder(1200, 600, project.title)} />
        {/* Gradient overlay — lighter at top, darker at bottom for text legibility */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(20,20,32,0.88) 0%, rgba(20,20,32,0.2) 55%, transparent 100%)' }} />
        {/* Subtle construction canvas lines on hero */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 3 }}>
          <ConstructionCanvas variant="grid" color={CRANE} opacity={0.25} />
        </div>
        {/* Breadcrumb + title */}
        <div style={{ position: 'absolute', bottom: 48, left: 0, right: 0, zIndex: 4 }}>
          <div className="container">
            <nav className="project-detail-hero-breadcrumb" style={{ marginBottom: 14 }}>
              <Link to="/"        style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>Home</Link>
              <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 8px' }}>/</span>
              <Link to="/projects" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>Projects</Link>
              <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 8px' }}>/</span>
              <span style={{ color: CRANE, fontSize: '0.85rem' }}>{project.title}</span>
            </nav>
            <div className="d-flex align-items-center gap-3 mb-3 flex-wrap project-detail-hero-badges">
              <span style={{ background: `rgba(211,152,22,0.18)`, color: CRANE, fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20, border: `1px solid rgba(211,152,22,0.3)` }}>
                {project.status}
              </span>
              {project.is_featured === 1 && (
                <span style={{ background: 'rgba(180,211,217,0.15)', color: '#B4D3D9', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                  <i className="bi bi-star-fill me-1" />Featured
                </span>
              )}
            </div>
            <h1 className="project-detail-hero-title" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem,4.5vw,3.5rem)', color: '#fff', margin: 0, animation: 'fadeInUp 0.6s ease both' }}>
              {project.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Main content — light background */}
      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '60px 0 20px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <ConstructionCanvas variant="blueprint" color={CRANE} opacity={0.3} />
        </div>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row g-5">
            {/* Description + gallery + services */}
            <div className="col-lg-8">
              <h4 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 18 }}>Project Overview</h4>
              <div style={{ color: 'var(--text-2)', lineHeight: 2, fontSize: '1.02rem', whiteSpace: 'pre-line' }}>
                {project.description}
              </div>

              {/* Gallery */}
              {allImages.length > 1 && (
                <div style={{ marginTop: 48 }}>
                  <h4 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 20 }}>Project Gallery</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 10 }}>
                    {allImages.map((url, i) => (
                      <div key={i} onClick={() => setLb({ open: true, index: i })}
                        style={{ borderRadius: 'var(--radius)', overflow: 'hidden', cursor: 'pointer', aspectRatio: '4/3', border: `1px solid rgba(211,152,22,0.15)`, transition: 'var(--transition)', boxShadow: 'var(--shadow-sm)' }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; e.currentTarget.style.borderColor = `rgba(211,152,22,0.4)`; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.borderColor = `rgba(211,152,22,0.15)`; }}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => e.target.src = getPlaceholder(400, 300)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Services */}
              {project.services?.length > 0 && (
                <div style={{ marginTop: 48 }}>
                  <h4 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 24 }}>Services Included</h4>
                  <div className="row g-3">
                    {project.services.map(s => (
                      <div key={s.id} className="col-sm-6"><ServiceCard service={s} /></div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar — project details */}
            <div className="col-lg-4">
              <div style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', border: `1px solid rgba(211,152,22,0.18)`, borderRadius: 'var(--radius-xl)', padding: 28, position: 'sticky', top: 100, boxShadow: 'var(--shadow)' }}>
                <h5 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 22, paddingBottom: 16, borderBottom: `1px solid rgba(211,152,22,0.12)` }}>
                  Project Details
                </h5>
                {[
                  { label: 'Client',      value: project.client_name,   icon: 'bi-person-fill' },
                  { label: 'Location',    value: project.location,      icon: 'bi-geo-alt-fill' },
                  { label: 'Status',      value: project.status?.charAt(0).toUpperCase() + project.status?.slice(1), icon: 'bi-check-circle-fill' },
                  { label: 'Start Date',  value: project.start_date  ? new Date(project.start_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null, icon: 'bi-calendar-check' },
                  { label: 'Completion', value: project.end_date    ? new Date(project.end_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Ongoing', icon: 'bi-calendar2-check-fill' },
                  { label: 'Value',       value: project.project_value ? `£${parseFloat(project.project_value).toLocaleString()}` : null, icon: 'bi-currency-pound' },
                ].filter(d => d.value).map(d => (
                  <div key={d.label} style={{ display: 'flex', gap: 12, marginBottom: 18, alignItems: 'flex-start' }}>
                    <i className={`bi ${d.icon}`} style={{ color: CRANE, fontSize: 16, marginTop: 2, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 2 }}>{d.label}</div>
                      <div style={{ color: 'var(--text)', fontSize: '0.95rem' }}>{d.value}</div>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 24, paddingTop: 22, borderTop: `1px solid rgba(211,152,22,0.12)` }}>
                  <Link to="/contact" className="btn-primary-pub w-100" style={{ justifyContent: 'center', fontSize: 13 }}>
                    <i className="bi bi-chat-text" /> Discuss Similar Project
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid rgba(211,152,22,0.1)` }}>
            <Link to="/projects" className="btn-outline-pub-dark" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-2)', border: '1.5px solid var(--border)', padding: '10px 22px', borderRadius: 50, fontSize: 13, fontWeight: 600, transition: 'var(--transition)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = CRANE; e.currentTarget.style.color = CRANE; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}>
              <i className="bi bi-arrow-left" /> Back to Projects
            </Link>
          </div>
        </div>
      </section>

      {lb.open && <Lightbox images={allImages} index={lb.index} onClose={() => setLb({ open: false, index: 0 })} />}
    </>
  );
}
